import { assign, fromPromise, setup } from "xstate";

import type { AuthProvisioningStatus } from "@elation/api-client/src/stitch-web";

import { toErrorMessage } from "@src/shared/utils/errors";

export type SignUpMachineAccountValues = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type SignUpProvisioningMachineInput = {
  createAccount: (
    values: SignUpMachineAccountValues,
  ) => Promise<SignUpAccountCreationResult>;
};

export type SignUpAccountCreationResult = {
  email: string;
  needsEmailVerification: boolean;
};

export type SignUpProvisioningMachineContext =
  SignUpProvisioningMachineInput & {
    accountError: string | null;
    verificationEmail: string | null;
    pendingStartedAt: number | null;
    provisioningStatus: AuthProvisioningStatus | null;
    unexpectedErrorReference: string | null;
  };

export type SignUpProvisioningMachineEvent =
  | { type: "SUBMIT_ACCOUNT"; values: SignUpMachineAccountValues }
  | { type: "EMAIL_VERIFIED" }
  | { type: "RESUME_PROVISIONING" }
  | { type: "PROVISIONING_STATUS"; status: AuthProvisioningStatus }
  | { type: "PROVISIONING_UNEXPECTED_ERROR"; requestId?: string }
  | { type: "PROVISIONING_UNAUTHORIZED" }
  | { type: "PROVISIONING_NOT_FOUND" }
  | { type: "PROVISIONING_TIMED_OUT" };

function getAccountCreationResult(
  event: unknown,
): SignUpAccountCreationResult | null {
  if (!event || typeof event !== "object" || !("output" in event)) {
    return null;
  }

  const output = (event as { output: unknown }).output;

  if (!output || typeof output !== "object") return null;

  const result = output as Partial<SignUpAccountCreationResult>;

  return typeof result.email === "string" &&
    typeof result.needsEmailVerification === "boolean"
    ? {
        email: result.email,
        needsEmailVerification: result.needsEmailVerification,
      }
    : null;
}

export const signUpProvisioningMachine = setup({
  types: {
    context: {} as SignUpProvisioningMachineContext,
    events: {} as SignUpProvisioningMachineEvent,
    input: {} as SignUpProvisioningMachineInput,
  },
  actors: {
    createAccount: fromPromise(
      async ({
        input,
      }: {
        input: SignUpProvisioningMachineInput & {
          values: SignUpMachineAccountValues;
        };
      }) => {
        return input.createAccount(input.values);
      },
    ),
  },
  actions: {
    clearAccountError: assign({
      accountError: null,
      unexpectedErrorReference: null,
    }),
    setAccountError: assign({
      accountError: ({ event }) =>
        "error" in event
          ? toErrorMessage(event.error, "Unable to create account.")
          : "Unable to create account.",
    }),
    startEmailVerification: assign({
      accountError: null,
      verificationEmail: ({ event }) =>
        getAccountCreationResult(event)?.email ?? null,
      unexpectedErrorReference: null,
    }),
    startProvisioning: assign({
      accountError: null,
      pendingStartedAt: () => Date.now(),
      provisioningStatus: null,
      unexpectedErrorReference: null,
    }),
    resumeProvisioning: assign({
      accountError: null,
      pendingStartedAt: ({ context }) => context.pendingStartedAt ?? Date.now(),
      unexpectedErrorReference: null,
    }),
    setProvisioningStatus: assign({
      provisioningStatus: ({ event }) =>
        event.type === "PROVISIONING_STATUS" ? event.status : null,
      unexpectedErrorReference: null,
    }),
    setUnexpectedErrorReference: assign({
      unexpectedErrorReference: ({ event }) =>
        event.type === "PROVISIONING_UNEXPECTED_ERROR"
          ? (event.requestId ?? null)
          : null,
    }),
  },
  guards: {
    needsEmailVerification: ({ event }) =>
      getAccountCreationResult(event)?.needsEmailVerification ?? false,
    provisioningSucceeded: ({ event }) =>
      event.type === "PROVISIONING_STATUS" &&
      event.status.state === "succeeded",
    provisioningFailed: ({ event }) =>
      event.type === "PROVISIONING_STATUS" && event.status.state === "failed",
  },
}).createMachine({
  id: "signUpProvisioning",
  context: ({ input }) => ({
    ...input,
    accountError: null,
    verificationEmail: null,
    pendingStartedAt: null,
    provisioningStatus: null,
    unexpectedErrorReference: null,
  }),
  initial: "idle",
  states: {
    idle: {
      on: {
        SUBMIT_ACCOUNT: {
          target: "creatingAccount",
          actions: "clearAccountError",
        },
        RESUME_PROVISIONING: {
          target: "provisioning.pending",
          actions: "resumeProvisioning",
        },
        EMAIL_VERIFIED: {
          target: "provisioning.pending",
          actions: "startProvisioning",
        },
      },
    },
    creatingAccount: {
      invoke: {
        src: "createAccount",
        input: ({ context, event }) => {
          if (event.type !== "SUBMIT_ACCOUNT") {
            throw new Error("Missing account values for sign-up.");
          }

          return {
            createAccount: context.createAccount,
            values: event.values,
          };
        },
        onDone: [
          {
            guard: "needsEmailVerification",
            target: "emailVerification.pending",
            actions: "startEmailVerification",
          },
          {
            target: "provisioning.pending",
            actions: "startProvisioning",
          },
        ],
        onError: {
          target: "accountCreationFailed",
          actions: "setAccountError",
        },
      },
    },
    accountCreationFailed: {
      on: {
        SUBMIT_ACCOUNT: {
          target: "creatingAccount",
          actions: "clearAccountError",
        },
      },
    },
    emailVerification: {
      initial: "pending",
      states: {
        pending: {
          on: {
            EMAIL_VERIFIED: {
              target: "#signUpProvisioning.provisioning.pending",
              actions: "startProvisioning",
            },
          },
        },
      },
    },
    provisioning: {
      initial: "pending",
      states: {
        pending: {
          on: {
            PROVISIONING_STATUS: [
              {
                guard: "provisioningSucceeded",
                target: "succeeded",
                actions: "setProvisioningStatus",
              },
              {
                guard: "provisioningFailed",
                target: "failed",
                actions: "setProvisioningStatus",
              },
              {
                actions: "setProvisioningStatus",
              },
            ],
            PROVISIONING_NOT_FOUND: {
              target: "succeeded",
            },
            PROVISIONING_UNAUTHORIZED: {
              target: "unauthorized",
            },
            PROVISIONING_UNEXPECTED_ERROR: {
              target: "failed",
              actions: "setUnexpectedErrorReference",
            },
            PROVISIONING_TIMED_OUT: {
              target: "timedOut",
            },
          },
        },
        succeeded: {},
        failed: {},
        timedOut: {},
        unauthorized: {},
      },
    },
  },
});
